import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import WebService from "../Services/WebService";
import { mediaUrl } from "../Services/mediaUrl";
import { FaSearch, FaTrash, FaEdit } from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import styles from "./RawMaterial.module.css";
import AddRawMaterialPanel from "./AddRawMaterialPanel";
import { navigateToBlade, useListBlade } from "../Customs/FullWidthBlade/FullWidthBlade";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { AccessItem } from "../Models/Models";
import type { RootState } from "../Services/Store";

interface RawMaterialImage {
  document_id: number;
  file_name: string;
}
interface RawMaterialItem {
  id: number;
  sku_id: string;
  name: string;
  measuring_unit: string;
  measuring_unit_value: number;
  quantity: number;
  category: string;
  is_trading_allowed: boolean;
  is_return_allowed: boolean;
  cost_price: number;
  selling_price: number;
  distributor_selling_price: number;
  retailer_selling_price: number;
  gst_percentage: number;
  hsn_code?: string | null;
  description: string;
  status: string | null;
  images: RawMaterialImage[];
}
interface Pagination {
  current_page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
}
interface Category {
  category_id: number;
  category_name: string;
}

const DEFAULT_LIMIT = 7;
const TABLE_COLS = 11;

/** Unwrap GET /raw-material/categories whether it returns a list or a paginated payload. */
function extractCategories(res: any): Category[] {
  const raw = Array.isArray(res?.data)
    ? res.data
    : Array.isArray(res?.data?.data)
    ? res.data.data
    : Array.isArray(res)
    ? res
    : [];
  return raw
    .filter((c: any) => c != null && c.category_id != null)
    .sort((a: Category, b: Category) =>
      String(a.category_name || "").localeCompare(String(b.category_name || ""), undefined, {
        sensitivity: "base",
      })
    );
}

const RawMaterial: React.FC = () => {
  // RBAC
  const accessList = useSelector((state: RootState) => state.auth.access || []);
  const inventoryAccess =
    (accessList.find((a: AccessItem) => a.code === "inventory_management") as AccessItem) ||
    ({} as AccessItem);
  const canRead = !!inventoryAccess.read;
  const canWrite = !!inventoryAccess.write;
  const canUpdate = !!inventoryAccess.update;
  const canDelete = !!inventoryAccess.delete;

  // Routing, Params, Navigation
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { outlet, bladeOpen } = useListBlade();

  // Local UI state
  const [rawMaterials, setRawMaterials] = useState<RawMaterialItem[]>([]);
  const [, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editMaterial, setEditMaterial] = useState<RawMaterialItem | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryError, setCategoryError] = useState<string>("");

  // For controlled search input (only UI, not filter)
  const [searchInput, setSearchInput] = useState<string>(searchParams.get("search") || "");

  // Toast Notification
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" | "" }>({
    message: "",
    type: "",
  });
  const showToast = (message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 2400);
  };

  // --- Derived filter values from URL ---
  const search = searchParams.get("search") || "";
  const statusFilter = searchParams.get("status") || "All";
  const categoryFilter = searchParams.get("category") || "All";
  const fromDate = searchParams.get("fromDate") || "";
  const toDate = searchParams.get("toDate") || "";

  // Infinite scroll state (invisible pagination)
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);

  // --- Category dropdown fetch (all non-deleted categories; no parent-only / page cap) ---
  useEffect(() => {
    WebService.getAPI<any>({
      action: "/v1/door-management/raw-material/categories",
    })
      .then((res) => {
        setCategories(extractCategories(res));
        setCategoryError("");
      })
      .catch(() => {
        setCategories([]);
        setCategoryError("Could not load categories.");
      });
  }, []);

  // --- Fetch a specific page; append + dedupe ---
  const fetchPage = useCallback(
    async (pageNo: number) => {
      setLoading(true);
      setError("");

      const queryParams = new URLSearchParams();
      queryParams.append("limit", DEFAULT_LIMIT.toString());
      queryParams.append("page_number", String(pageNo));
      queryParams.append("sort_order", "desc");

      if (search) queryParams.append("rm_like", search);
      if (statusFilter !== "All") queryParams.append("status", statusFilter);
      if (categoryFilter !== "All" && categoryFilter !== "")
        queryParams.append("category_id", categoryFilter);
      if (fromDate) queryParams.append("from_date", fromDate);
      if (toDate) queryParams.append("to_date", toDate);

      try {
        const res = await WebService.getAPI<any>({
          action: `/v1/door-management/raw-material?${queryParams.toString()}`,
        });
        const data = res.data?.data || res.data || {};
        const pageItems: RawMaterialItem[] = data.raw_materials || [];
        const pg: Pagination | null = data.pagination || null;

        setPagination(pg);

        setRawMaterials((prev) => {
          const next = pageNo === 1 ? pageItems : [...prev, ...pageItems];
          const seen = new Set<number>();
          return next.filter((rm) => {
            if (seen.has(rm.id)) return false;
            seen.add(rm.id);
            return true;
          });
        });

        if (pg) {
          setHasMore(pg.current_page < pg.total_pages);
        } else {
          setHasMore(pageItems.length >= DEFAULT_LIMIT);
        }
      } catch {
        setError("Failed to load raw materials.");
        if (pageNo === 1) setRawMaterials([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [search, statusFilter, categoryFilter, fromDate, toDate]
  );

  // Reset list on filter changes
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setRawMaterials([]);
    fetchPage(1);
  }, [fetchPage]);

  // Load next page when page increments
  useEffect(() => {
    if (page > 1) fetchPage(page);
  }, [page, fetchPage]);

  // IntersectionObserver to trigger load-more
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || bladeOpen) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loading) {
          setPage((p) => p + 1);
        }
      },
      { root: null, rootMargin: "200px", threshold: 0 }
    );

    obs.observe(node);
    return () => {
      obs.unobserve(node);
      obs.disconnect();
    };
  }, [hasMore, loading, bladeOpen]);

  // --- Filter and search handlers update URL params only ---
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams.toString());
    if (searchInput) params.set("search", searchInput);
    else params.delete("search");
    setSearchParams(params);
  };
  const handleStatusFilter = (val: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (val && val !== "All") params.set("status", val);
    else params.delete("status");
    setSearchParams(params);
  };
  const handleCategoryFilter = (val: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (val && val !== "All") params.set("category", val);
    else params.delete("category");
    setSearchParams(params);
  };
  const handleFromDateChange = (val: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (val) params.set("fromDate", val);
    else params.delete("fromDate");
    setSearchParams(params);
  };
  const handleToDateChange = (val: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (val) params.set("toDate", val);
    else params.delete("toDate");
    setSearchParams(params);
  };

  // --- RBAC: Add/Edit/Delete Material Logic ---
  const handleAddMaterial = () => {
    if (!canWrite) {
      showToast("You do not have permission to add material.", "error");
      return;
    }
    setShowAddPanel(true);
    setEditMaterial(null);
  };
  const handleEditMaterial = (material: RawMaterialItem) => {
    if (!canUpdate) {
      showToast("You do not have permission to edit material.", "error");
      return;
    }
    setEditMaterial(material);
    setShowAddPanel(true);
  };
  const handleDeleteMaterial = async (id: number) => {
    if (!canDelete) {
      showToast("You do not have permission to delete material.", "error");
      return;
    }
    const confirm = await Swal.fire({
      title: "Delete Material",
      text: "Are you sure you want to delete this material?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#FFD400",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      heightAuto: false,
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;
    try {
      setLoading(true);
      await WebService.deleteAPI({
        action: `/v1/door-management/raw-material/${id}`,
      });
      // Refresh from page 1 to keep list consistent
      setPage(1);
      setHasMore(true);
      setRawMaterials([]);
      await fetchPage(1);
      showToast("Material deleted successfully!", "success");
    } catch (err: any) {
      showToast(
        err?.response?.data?.message || err?.message || "Failed to delete material.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (id: number) => {
    navigateToBlade(navigate, `/raw-material/${id}`, searchParams);
  };

  const getStatusChip = (qty: number) => {
    if (qty === 0) return <span style={{ color: "red" }}>Low Stock</span>;
    if (qty > 0) return <span style={{ color: "green" }}>In Stock</span>;
    return <span>Unknown</span>;
  };

  // Close add/edit panel and optionally refresh list
  const handlePanelClose = (refresh = false) => {
    setShowAddPanel(false);
    setEditMaterial(null);
    if (refresh) {
      setPage(1);
      setHasMore(true);
      setRawMaterials([]);
      fetchPage(1);
    }
  };

  if (!canRead && !bladeOpen) {
    return (
      <div style={{ padding: 40, color: "red", fontWeight: 600, fontSize: 22 }}>
        You do not have access to view Raw Materials.
      </div>
    );
  }

  if (!canRead && bladeOpen) {
    return <>{outlet}</>;
  }

  return (
    <div className={styles.rawMaterialWrapper}>
      <div inert={bladeOpen ? true : undefined} aria-hidden={bladeOpen || undefined}>
      {/* Toast Notification */}
      {toast.message && (
        <div
          style={{
            position: "fixed",
            top: 24,
            right: 32,
            zIndex: 9999,
            minWidth: 260,
            background: toast.type === "success" ? "#FFD400" : "#e43e2d",
            color: toast.type === "success" ? "#232323" : "#fff",
            fontWeight: 600,
            fontSize: 15,
            borderRadius: 12,
            boxShadow: "0 4px 18px rgba(40,40,40,0.08)",
            padding: "15px 30px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            animation: "slideInToast 0.33s",
          }}
        >
          {toast.type === "success" ? "✔️" : "❌"}&nbsp;{toast.message}
        </div>
      )}

      {/* --- Title, Tab Buttons --- */}
      <div className={styles.headerRow}>
        <span className={styles.pageTitle}>Raw Material</span>
        <div className={styles.buttonRow}>
          <button className={`${styles.bigBtn} ${styles.active}`}>Raw Material</button>
          <button
            className={styles.bigBtn}
            onClick={() => (window.location.href = "/inventory/raw-material-categories")}
          >
            Raw Material Categories
          </button>
        </div>
      </div>

      {/* --- Filter/Search Row --- */}
      <div className={styles.searchAndFilterRow}>
        <form className={styles.searchForm} onSubmit={handleSearch}>
          <FaSearch className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by Material Name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </form>
        <select
          className={styles.filterDropdown}
          value={statusFilter}
          onChange={(e) => handleStatusFilter(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
        <select
          className={styles.filterDropdown}
          value={categoryFilter}
          onChange={(e) => handleCategoryFilter(e.target.value)}
        >
          <option value="All">All Categories</option>
          {categories.map((cat) => (
            <option key={cat.category_id} value={cat.category_id.toString()}>
              {cat.category_name}
            </option>
          ))}
        </select>
        {categoryError && (
          <span style={{ color: "red", marginLeft: 8 }}>{categoryError}</span>
        )}
        <input
          type="date"
          className={styles.filterDateInput}
          value={fromDate}
          onChange={(e) => handleFromDateChange(e.target.value)}
          placeholder="From Date"
          max={toDate || undefined}
        />
        <input
          type="date"
          className={styles.filterDateInput}
          value={toDate}
          onChange={(e) => handleToDateChange(e.target.value)}
          placeholder="To Date"
          min={fromDate || undefined}
        />
        {/* Add New Material button now at right */}
        <div className={styles.addBtnWrapper} style={{ marginLeft: "auto" }}>
          {canWrite ? (
            <button className={styles.addBtn} onClick={handleAddMaterial} type="button">
              <MdAdd style={{ marginRight: 8, fontSize: "1.35rem" }} />
              Add New Material
            </button>
          ) : (
            <button
              className={styles.addBtn}
              disabled
              style={{ opacity: 0.5, cursor: "not-allowed" }}
              title="You do not have permission to add materials"
              type="button"
            >
              <MdAdd style={{ marginRight: 8, fontSize: "1.35rem" }} />
              Add New Material
            </button>
          )}
        </div>
      </div>

      {/* --- Table --- */}
      <div className={styles.materialTableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Image</th>
              <th>SKU Number</th>
              <th>HSN Code</th>
              <th>Material Name</th>
              <th>Quantity</th>
              <th>Unit</th>
              <th>Purchase Price</th>
              <th>Sale Price</th>
              <th>GST%</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {error ? (
              <tr>
                <td colSpan={TABLE_COLS} className={styles.errorText} style={{ textAlign: "center", padding: 32 }}>
                  {error}
                </td>
              </tr>
            ) : rawMaterials.length === 0 && !loading ? (
              <tr>
                <td colSpan={TABLE_COLS} style={{ textAlign: "center", padding: 38, color: "#666", fontSize: 16 }}>
                  No Raw Materials Found.
                </td>
              </tr>
            ) : (
              <>
                {rawMaterials.map((item) => (
                  <tr
                    key={item.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRowClick(item.id)}
                  >
                    {/* Image */}
                    <td>
                      <img
                        src={
                          item.images?.[0]?.file_name
                            ? mediaUrl(item.images[0].file_name)
                            : process.env.PUBLIC_URL + "/Assets/no-pictures.png"
                        }
                        alt={item.name}
                        className={
                          item.images?.[0]?.file_name
                            ? styles.materialImage
                            : `${styles.materialImage} ${styles.noImage}`
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(item.id);
                        }}
                      />
                    </td>

                    {/* SKU Number */}
                    <td>{item.sku_id}</td>

                    {/* HSN Code */}
                    <td>
                      {item.hsn_code && item.hsn_code.toString().trim().toLowerCase() !== "null"
                        ? item.hsn_code
                        : "-"}
                    </td>

                    {/* Material Name (with category & description) */}
                    <td>
                      <b>{item.name}</b>
                      <div>{item.category}</div>
                      <div>{item.description}</div>
                    </td>

                    {/* Quantity */}
                    <td>{item.quantity}</td>

                    {/* Unit */}
                    <td>{item.measuring_unit}</td>

                    {/* Purchase Price */}
                    <td>₹{item.cost_price}</td>

                    {/* Sale Price */}
                    <td>₹{item.selling_price || item.retailer_selling_price}</td>

                    {/* GST% */}
                    <td>{item.gst_percentage}%</td>

                    {/* Status */}
                    <td>{getStatusChip(item.quantity)}</td>

                    {/* Actions */}
                    <td onClick={(e) => e.stopPropagation()}>
                      {canUpdate ? (
                        <FaEdit
                          className={styles.editIcon}
                          onClick={() => handleEditMaterial(item)}
                          title="Edit"
                        />
                      ) : (
                        <FaEdit className={styles.editIcon} title="You do not have permission to edit" />
                      )}
                      {canDelete ? (
                        <FaTrash
                          className={styles.trashIcon}
                          style={{ cursor: "pointer", color: "#e43e2d" }}
                          onClick={() => handleDeleteMaterial(item.id)}
                          title="Delete"
                        />
                      ) : (
                        <FaTrash
                          className={styles.trashIcon}
                          style={{ opacity: 0.4, cursor: "not-allowed" }}
                          title="You do not have permission to delete"
                        />
                      )}
                    </td>
                  </tr>
                ))}

                {/* Sentinel row for invisible pagination */}
                <tr ref={sentinelRef}>
                  <td colSpan={TABLE_COLS} style={{ textAlign: "center", padding: 16, color: "#666" }}>
                    {loading ? "Loading…" : hasMore ? "Loading more…" : "— End —"}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
      {showAddPanel && (
        <div className={styles.overlay}>
          <AddRawMaterialPanel
            onClose={handlePanelClose}
            editMaterial={editMaterial}
            showToast={showToast}
            canWrite={canWrite}
            canUpdate={canUpdate}
          />
        </div>
      )}
      </div>
      {outlet}
    </div>
  );
};

export default RawMaterial;
